import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import "./PortfolioPrint.css";

/**
 * Print-optimized version of the portfolio. Opens in a new tab from the
 * "Baixar Catálogo" button; auto-triggers window.print() after content loads.
 * Users choose "Save as PDF" in the print dialog. Zero server-side dependency.
 */

const TRANSPORT_LABEL = {
  rodoviario: "Ônibus-leito · Loja",
  aereo_nac: "Voo Nacional",
  aereo_int: "Voo Internacional",
};

function formatBRL(v) {
  if (!v) return null;
  return "R$ " + v.toLocaleString("pt-BR");
}

export default function PortfolioPrint() {
  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [ready, setReady] = useState(false);

  // Query params — enable the "Proposta Comercial" mode (Minha Lista + ROI)
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode") || "catalogo"; // "catalogo" | "lista"
  const idsParam = params.get("ids") || "";
  const idsSet = new Set(idsParam.split(",").filter(Boolean));
  const companyName = params.get("empresa") || "";
  const isProposta = mode === "lista" && idsSet.size > 0;

  usePageMeta(
    isProposta
      ? `Proposta Comercial · Orion${companyName ? " — " + companyName : ""}`
      : "Catálogo Orion · Imprimir",
    ""
  );

  useEffect(() => {
    api.get("/portfolio").then((r) => {
      setSections(r.data?.sections || []);
      setTotal(r.data?.total_destinations || 0);
      setStats(r.data?.stats || null);
    });
  }, []);

  // For proposta mode: filter each section's items to only the selected ids
  const displaySections = isProposta
    ? sections
        .map((s) => ({ ...s, items: s.items.filter((it) => idsSet.has(it.id)) }))
        .filter((s) => s.items.length > 0)
        .map((s) => ({ ...s, count: s.items.length }))
    : sections;
  const propostaTotal = isProposta
    ? [...new Set(displaySections.flatMap((s) => s.items.map((i) => i.id)))].length
    : total;

  // Wait for images to load, then auto-invoke print dialog
  useEffect(() => {
    if (sections.length === 0) return;
    const t = setTimeout(async () => {
      const imgs = Array.from(document.images);
      await Promise.all(
        imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
        })))
      );
      setReady(true);
      setTimeout(() => window.print(), 500);
    }, 600);
    return () => clearTimeout(t);
  }, [sections]);

  return (
    <div className={`print-root ${isProposta ? "proposta" : ""}`} data-testid="portfolio-print">
      <div className="print-cover">
        {isProposta ? (
          <>
            <div className="print-eyebrow">Viagens Orion · Proposta Comercial 2026</div>
            <h1 className="print-title">Proposta<br />Corporativa</h1>
            {companyName && (
              <div className="print-company" data-testid="print-company">
                Preparada para <strong>{companyName}</strong>
              </div>
            )}
            <p className="print-sub">
              {propostaTotal} destino{propostaTotal !== 1 ? "s" : ""} selecionado{propostaTotal !== 1 ? "s" : ""} sob medida
              para sua equipe. Toda experiência inclui Transfer IN/OUT com motorista
              bilíngue, seguro-viagem e hospedagem bem localizada.
            </p>
            <div className="print-cover-note">
              * Grupos a partir de 10 pessoas: os valores estimados dos pacotes podem
              ter até <strong>60% de desconto</strong>. Valores por pessoa em alta temporada.
            </div>
          </>
        ) : (
          <>
            <div className="print-eyebrow">Viagens Orion · Roteiros 2026</div>
            <h1 className="print-title">Portfólio de<br />Experiências</h1>
            <p className="print-sub">
              {total} destinos desenhados sob medida.
              Toda experiência inclui passagem aérea ida/volta, Transfer IN/OUT
              com motorista bilíngue, seguro-viagem e hospedagem bem localizada.
            </p>
            <div className="print-cover-note">
              * Passeios e atrações são opcionais, oferecidos via agências parceiras.
              Valores estimados em alta temporada, por pessoa.
            </div>
          </>
        )}
        <div className="print-signature">
          <strong>Viagens Orion</strong> · Credenciada pelo Ministério do Turismo · Cadastur
          <br />
          <span style={{ opacity: 0.7, fontSize: "11px" }}>
            S R Scabio Operadora e Agência de Viagens - ME · CNPJ 37.476.039/0001-90
          </span>
        </div>
        <button
          className="print-btn no-print"
          onClick={() => window.print()}
          style={{ marginTop: 40 }}
        >
          {ready ? "Reimprimir" : "Preparando…"}
        </button>
      </div>

      {/* ROI Block — appears only in "Proposta" mode, between cover and destinations */}
      {isProposta && (
        <section className="print-section print-roi-page" data-testid="print-roi">
          <header className="print-section-head">
            <h2>Por que Orion? Retorno sobre o Investimento</h2>
            <span>Transparência corporativa</span>
          </header>
          <div className="print-roi-grid">
            <div className="print-roi-card">
              <div className="print-roi-tag">📈 Incentivo · Premiação</div>
              <ul>
                <li><strong>Alavancagem de faturamento:</strong> a meta agressiva paga a viagem e sobra lucro.</li>
                <li><strong>Retenção de talentos:</strong> reduz turnover — substituir um talento custa caro.</li>
                <li><strong>Engajamento:</strong> impacto direto no clima e produtividade do time.</li>
              </ul>
            </div>
            <div className="print-roi-card">
              <div className="print-roi-tag">🤝 Negócios · Feiras · Convenções</div>
              <ul>
                <li><strong>Fechamento presencial:</strong> reunião olho-no-olho converte mais do que vídeo.</li>
                <li><strong>Prospecção qualificada:</strong> estar no polo do seu setor abre pipeline futuro.</li>
                <li><strong>Treinamento e inovação:</strong> traz produtos e otimizações internas.</li>
              </ul>
            </div>
          </div>
          <div className="print-roi-example">
            <strong>Grupos a partir de 10 pessoas têm até 60% de desconto</strong> sobre
            os valores estimados. Consulte nossa curadoria para roteiros personalizados.
          </div>
        </section>
      )}

      {displaySections.map((section) => (
        <section key={section.id} className="print-section">
          <header className="print-section-head">
            <h2>{section.title}</h2>
            <span>{section.count} {section.count === 1 ? "experiência" : "experiências"}</span>
          </header>
          <div className="print-grid">
            {section.items.map((it) => (
              <article key={it.id} className="print-card">
                {it.image && <img src={it.image} alt={it.name} className="print-img" />}
                <div className="print-body">
                  <div className="print-card-region">{it.region?.replace("-", " · ")}</div>
                  <h3>{it.name.replace(/\s+[A-Z]{2}\s*$/, "")}</h3>
                  <div className="print-tags">
                    <span>{it.days} dias</span>
                    <span>{TRANSPORT_LABEL[it.transport]}</span>
                    {it.preferido && <span className="print-tag-preferido">★ Preferido</span>}
                    {it.tags?.includes("filhos") && <span>Família</span>}
                    {it.tags?.includes("romantica") && <span>Romântica</span>}
                  </div>
                  <p className="print-pitch">{it.pitch}</p>
                  {it.highlights?.length > 0 && (
                    <div className="print-highlights">
                      {it.highlights.slice(0, 5).join(" · ")}
                    </div>
                  )}
                  <div className="print-price">
                    {it.price_from ? (
                      <>A partir de {it.price_estimated && <em>(est.)</em>} <strong>{formatBRL(it.price_from)}</strong></>
                    ) : (
                      <>Sob consulta</>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}

      <div className="print-back-cover">
        <h2>
          {isProposta
            ? "Sua próxima viagem corporativa começa aqui."
            : "Uma experiência Orion nunca é apenas um destino."}
        </h2>
        <p>
          Cada roteiro é desenhado individualmente. Transfer aeroporto-hotel-aeroporto
          com motorista bilíngue, seguro-viagem com cobertura ampliada, hospedagem
          bem localizada e um consultor Orion antes, durante e depois da viagem.
        </p>

        {!isProposta && stats && (
          <div className="print-stats" data-testid="print-stats">
            <div className="print-stats-eyebrow">Panorama do catálogo</div>
            <div className="print-stats-row">
              <div className="print-stat">
                <strong>{stats.unique_destinations}</strong>
                <span>destinos únicos</span>
              </div>
              <div className="print-stat">
                <strong>R$ {stats.avg_ticket?.toLocaleString("pt-BR")}</strong>
                <span>ticket médio estimado</span>
              </div>
              <div className="print-stat">
                <strong>{stats.preferidos}</strong>
                <span>preferidos da equipe</span>
              </div>
            </div>
            {stats.by_continent?.length > 0 && (
              <div className="print-stats-continents">
                <div className="print-stats-eyebrow small">Destinos por região</div>
                <div className="print-stats-chips">
                  {stats.by_continent.map((c) => (
                    <span key={c.label} className="print-stat-chip">
                      {c.label} <em>{c.count}</em>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="print-signature-back">
          <strong>Viagens Orion</strong> · Cadastur · SR SCABIO OPERADORA · CNPJ 37.476.039/0001-90
        </div>
      </div>
    </div>
  );
}
